import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["appearance", "active", "alignLabel", "interactive", "icon", "label", "meta", "step", "renderLabel"];
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import { ORIENTATIONS, PROGRESS_STEPPER_APPEARANCES, PROGRESS_STEPPER_LABEL_ALIGNMENTS } from '../../constants';
import CustomPropTypes from '../../util/custom-prop-types';
import { statusIconAppearance } from '../../util/statusIconAppearance';
import { ProgressStepperStepDot } from '../ProgressStepperStepDot/ProgressStepperStepDot';
import { ProgressStepperStepLabel } from '../ProgressStepperStepLabel/ProgressStepperStepLabel';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';
import { StyledProgressStepperStep } from './ProgressStepperStep.styles';

/**
 * A ProgressStepperStep component is used by `ProgressStepper` to identify a certain step.
 *
 * @since 8.42.0
 */
export var ProgressStepperStep = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var appearance = props.appearance,
    active = props.active,
    alignLabel = props.alignLabel,
    interactive = props.interactive,
    icon = props.icon,
    label = props.label,
    meta = props.meta,
    step = props.step,
    renderLabel = props.renderLabel,
    rest = _objectWithoutProperties(props, _excluded);
  var iconContent;
  if (appearance && !active) {
    if (typeof icon === 'function') {
      iconContent = icon({
        appearance: appearance
      });
    } else {
      iconContent = icon;
    }
  }

  // Since we represent appearance using icons, include a description
  // for screen readers.
  var iconAltText = '';
  if (typeof appearance !== 'undefined' && appearance !== 'none') {
    iconAltText = /*#__PURE__*/React.createElement(VisuallyHidden, null, appearance);
  }
  return /*#__PURE__*/React.createElement(StyledProgressStepperStep, _extends({
    ref: forwardedRef,
    as: interactive ? undefined : 'div',
    alignLabel: alignLabel,
    appearance: appearance,
    active: active,
    "aria-current": active ? 'step' : undefined,
    interactive: interactive
    // Only `null` works to override UnstyledButton's defaultProps for `type`
    ,
    type: interactive ? 'button' : null
  }, rest), /*#__PURE__*/React.createElement(ProgressStepperStepDot, null, iconAltText, iconContent), renderLabel(props));
});
ProgressStepperStep.displayName = 'ProgressStepperStep';
ProgressStepperStep.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Indicates this is the current, active step.
   */
  active: PropTypes.bool,
  /**
   * Sets the horizontal alignment for the label.
   *
   * All labels in a `ProgressStepper` must have the same alignment. Therefore,
   * alignment is determined by the parent `ProgressStepper` and cannot be
   * overridden for an individual step.
   *
   * @since 8.57.0
   * @ignore
   */
  alignLabel: PropTypes.oneOf(Object.values(PROGRESS_STEPPER_LABEL_ALIGNMENTS)),
  /**
   * Changes the appearance of a step to show its status. If no appearance is
   * provided, the step will appear in the default, "not started" state.
   */
  appearance: PropTypes.oneOf(Object.values(PROGRESS_STEPPER_APPEARANCES)),
  /**
   * The icon node. By default, this will use an icon based on the `appearance`.
   */
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /**
   * If true, user will be able to click on and navigate to the step.
   */
  interactive: PropTypes.bool,
  /**
   * Label text of the step.
   */
  label: PropTypes.string.isRequired,
  /**
   * Additional content that will appear underneath the step label text. Currently,
   * `Tag` and text are officially supported. For more
   * customization options, you can use the `renderLabel` prop.
   *
   * It's best to avoid long `meta` values to help maintain understandability
   * for screen reader users.
   *
   * @since 8.57.0
   */
  meta: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Changes the step style to work with either the horizontal or vertical version
   * of the `ProgressStepper`.
   *
   * @since 8.57.0
   * @ignore
   */
  orientation: CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(Object.values(ORIENTATIONS))),
  /**
   * A function that takes a props object as an argument and returns a step label
   * component, by default a `ProgressStepperStepLabel`.
   */
  renderLabel: PropTypes.func
} : {};
ProgressStepperStep.defaultProps = {
  alignLabel: PROGRESS_STEPPER_LABEL_ALIGNMENTS.CENTER,
  icon: statusIconAppearance,
  orientation: ORIENTATIONS.horizontal,
  renderLabel: function renderLabel(props) {
    return /*#__PURE__*/React.createElement(ProgressStepperStepLabel, props);
  }
};